/**
 * ScreensaverController — React UI control panel for the screensaver biome tour.
 *
 * Rips the ArenaPanel / AbilityGroundsPanel visual family (dark diagnostic-panel
 * chrome, monospace, fixed-position). Gates to /screensaver mode only. Wired to
 * the live ScreensaverSystem instance via ServiceRegistry (the system is
 * registered there by GameKernel.registerSystem → ServiceRegistry.register).
 *
 * Controls:
 *   ▶/⏸  pause/resume cycling
 *   ⏭    skip to next biome
 *   🔧    zoom toggle (0.8 ↔ 1.1)
 *   🔇    toggle biome-name caption overlay
 *   🌍   zone picker — jump directly to any biome in the cycle
 *   📊   current biome + time-remaining indicator (polled @ 4Hz)
 *
 * The panel auto-hides after 8s of pointer inactivity (stream-clean); moving
 * the mouse or touching the panel reveals it again. This keeps the stream
 * viewport clean while still giving DJ an interactive surface on demand.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { isScreensaverMode } from '../../../config/screensaver/isScreensaverMode';
import { SCREENSAVER_BIOME_CYCLE } from '../../../config/screensaver/screensaverCycle';

const AUTO_HIDE_MS = 8000;
const POLL_MS = 250; // 4Hz — enough resolution for the time-remaining bar

export const ScreensaverController: React.FC = () => {
  if (!isScreensaverMode()) return null;

  return <ScreensaverControllerInner />;
};

/** Inner component — separated so the isScreensaverMode() gate short-circuits
 *  before any hooks fire (rules-of-hooks: the outer early-return is stable for
 *  the session, but this split makes the intent explicit + lint-clean). */
const ScreensaverControllerInner: React.FC = () => {
  const [visible, setVisible] = useState(true);
  const [tick, setTick] = useState(0); // forces re-render for the indicator poll
  const [showZones, setShowZones] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSystem = useCallback(() => {
    return ServiceRegistry.getInstance().get('ScreensaverSystem', { optional: true });
  }, []);

  // --- auto-hide on inactivity ---
  const reveal = useCallback(() => {
    setVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
  }, []);

  useEffect(() => {
    reveal();
    // Reveal on any pointer move over the viewport (stream-clean UX).
    const onMove = () => reveal();
    window.addEventListener('pointermove', onMove);
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [reveal]);

  // --- indicator poll ---
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  // tick is read implicitly via the system getters on each render
  void tick;

  const sys = getSystem();

  // --- button handlers (defensive — system may briefly be absent during boot) ---
  const onPauseResume = () => {
    const s = getSystem();
    if (!s) return;
    if (s.getPhase() === 'touring') s.pause();
    else if (s.getPhase() === 'paused') s.resume();
  };
  const onSkip = () => getSystem()?.skipToNext();
  const onZoomToggle = () => getSystem()?.toggleZoom();
  const onOverlayToggle = () => getSystem()?.toggleOverlay();
  const onJumpToBiome = (index: number) => {
    getSystem()?.jumpToBiome(index);
    setShowZones(false); // collapse after picking
  };

  // --- indicator readout ---
  const phase = sys?.getPhase() ?? 'touring';
  const isPaused = phase === 'paused';
  const biomeLabel = sys?.getCurrentBiomeLabel() ?? '—';
  const elapsed = sys?.getElapsedInStopS() ?? 0;
  const duration = sys?.getCurrentStopDurationS() ?? 1;
  const remaining = Math.max(0, duration - elapsed);
  const progressPct = Math.min(100, (elapsed / duration) * 100);
  const zoomMode = sys?.getZoomMode() ?? 'wide';
  const overlayOn = sys?.isOverlayEnabled() ?? true;
  const currentBiomeIndex = sys?.getCurrentBiomeIndex() ?? 0;

  return (
    <div
      data-testid="screensaver-controller"
      style={{
        ...panel,
        opacity: visible ? 1 : 0,
        transition: 'opacity 400ms ease-in-out',
        pointerEvents: visible ? 'auto' : 'none',
      }}
      onPointerEnter={reveal}
    >
      <div style={header}>🎬 Screensaver</div>

      {/* Control buttons */}
      <div style={chipRow}>
        <button
          style={isPaused ? resumeBtn : pauseBtn}
          title={isPaused ? 'Resume cycling' : 'Pause cycling'}
          data-testid="screensaver-ctrl-pause"
          onClick={onPauseResume}
        >
          {isPaused ? '▶ Resume' : '⏸ Pause'}
        </button>
        <button
          style={skipBtn}
          title="Skip to next biome"
          data-testid="screensaver-ctrl-skip"
          onClick={onSkip}
        >
          ⏭ Skip
        </button>
        <button
          style={zoomBtn}
          title={zoomMode === 'wide' ? 'Wide (0.8) — click for close (1.1)' : 'Close (1.1) — click for wide (0.8)'}
          data-testid="screensaver-ctrl-zoom"
          onClick={onZoomToggle}
        >
          🔧 {zoomMode === 'wide' ? '0.8' : '1.1'}
        </button>
        <button
          style={overlayOn ? overlayOnBtn : overlayOffBtn}
          title={overlayOn ? 'Caption visible — click to hide' : 'Caption hidden — click to show'}
          data-testid="screensaver-ctrl-overlay"
          onClick={onOverlayToggle}
        >
          {overlayOn ? '🔊' : '🔇'}
        </button>
        <button
          style={showZones ? zonesActiveBtn : zonesBtn}
          title="Select biome to jump to"
          data-testid="screensaver-ctrl-zones"
          onClick={() => setShowZones(!showZones)}
        >
          🌍 Zones
        </button>
      </div>

      {/* Zone picker — collapsible list of all biomes in the cycle */}
      {showZones && (
        <div style={zoneList} data-testid="screensaver-zone-list">
          {SCREENSAVER_BIOME_CYCLE.map((stop, i) => (
            <button
              key={stop.mapId}
              style={i === currentBiomeIndex ? zoneItemActive : zoneItem}
              data-testid={`screensaver-zone-${i}`}
              onClick={() => onJumpToBiome(i)}
              title={`${stop.biome} — ${stop.durationS}s dwell`}
            >
              <span style={zoneDot(i === currentBiomeIndex)} />
              {stop.label}
            </button>
          ))}
        </div>
      )}

      {/* Biome indicator + time-remaining bar */}
      <div style={indicatorSection}>
        <div style={indicatorRow}>
          <span style={indicatorLabel}>biome</span>
          <span style={indicatorValue}>{biomeLabel}</span>
        </div>
        <div style={indicatorRow}>
          <span style={indicatorLabel}>{isPaused ? 'paused' : 'remaining'}</span>
          <span style={indicatorValue}>
            {isPaused ? '—' : `${remaining.toFixed(0)}s`}
          </span>
        </div>
        {/* Progress bar */}
        <div style={progressTrack}>
          <div
            style={{
              ...progressFill,
              width: `${progressPct}%`,
              background: isPaused ? '#555' : 'linear-gradient(90deg, #4a8, #6cb)',
            }}
          />
        </div>
      </div>

      <div style={footer}>Move mouse to reveal · auto-hides after {AUTO_HIDE_MS / 1000}s</div>
    </div>
  );
};

// ---- styles (consistent with the ArenaPanel / dark diagnostic-panel family) ----
const panel: React.CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: 16,
  width: 240,
  zIndex: 9001,
  background: 'rgba(16,18,26,0.92)',
  border: '1px solid #3a4256',
  borderRadius: 8,
  color: '#e6e6e6',
  fontFamily: 'monospace',
  fontSize: 12,
  padding: 10,
  boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
};
const header: React.CSSProperties = {
  fontWeight: 'bold',
  fontSize: 13,
  marginBottom: 8,
  borderBottom: '1px solid #2a3142',
  paddingBottom: 6,
};
const chipRow: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginBottom: 8,
};
const baseBtn: React.CSSProperties = {
  border: '1px solid #3a4256',
  borderRadius: 4,
  padding: '5px 8px',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'monospace',
};
const pauseBtn: React.CSSProperties = { ...baseBtn, background: '#3a2430', color: '#f5d8df', borderColor: '#7a3650' };
const resumeBtn: React.CSSProperties = { ...baseBtn, background: '#1e3a28', color: '#d8f5e0', borderColor: '#367a50' };
const skipBtn: React.CSSProperties = { ...baseBtn, background: '#243044', color: '#dfe8f5', borderColor: '#36507a' };
const zoomBtn: React.CSSProperties = { ...baseBtn, background: '#2a2a3a', color: '#d8d8f5', borderColor: '#4a4a6a' };
const overlayOnBtn: React.CSSProperties = { ...baseBtn, background: '#1e3a28', color: '#d8f5e0', borderColor: '#367a50' };
const overlayOffBtn: React.CSSProperties = { ...baseBtn, background: '#3a3a3a', color: '#888', borderColor: '#555' };
const zonesBtn: React.CSSProperties = { ...baseBtn, background: '#2a2438', color: '#d8d0f5', borderColor: '#4a3a6a' };
const zonesActiveBtn: React.CSSProperties = { ...baseBtn, background: '#3a2848', color: '#e8d8f5', borderColor: '#6a3a8a' };

const zoneList: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  marginBottom: 8,
  maxHeight: 200,
  overflowY: 'auto',
  borderTop: '1px solid #2a3142',
  paddingTop: 6,
};
const zoneItem: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  border: '1px solid transparent',
  borderRadius: 4,
  background: 'transparent',
  color: '#aaa',
  cursor: 'pointer',
  fontSize: 11,
  fontFamily: 'monospace',
  textAlign: 'left' as const,
  width: '100%',
};
const zoneItemActive: React.CSSProperties = {
  ...zoneItem,
  background: 'rgba(74, 136, 170, 0.15)',
  border: '1px solid #4a8aa8',
  color: '#cfe8f5',
  fontWeight: 600,
};
const zoneDot = (active: boolean): React.CSSProperties => ({
  display: 'inline-block',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: active ? '#4a8' : '#445',
  flexShrink: 0,
});

const indicatorSection: React.CSSProperties = {
  borderTop: '1px solid #2a3142',
  paddingTop: 8,
};
const indicatorRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '2px 0',
};
const indicatorLabel: React.CSSProperties = { color: '#778', fontStyle: 'italic' };
const indicatorValue: React.CSSProperties = { color: '#ccc', fontWeight: 600 };

const progressTrack: React.CSSProperties = {
  marginTop: 6,
  height: 4,
  background: '#1a1d28',
  borderRadius: 2,
  overflow: 'hidden',
};
const progressFill: React.CSSProperties = {
  height: '100%',
  borderRadius: 2,
  transition: 'width 250ms linear',
};

const footer: React.CSSProperties = {
  color: '#556',
  fontSize: 10,
  borderTop: '1px solid #2a3142',
  paddingTop: 6,
  marginTop: 8,
};
